/**
 * Synchronous tool-routing heuristics for UI workflows.
 * Mirrors open_ui / clarify rules from backend tool_router.py so the frontend
 * can open modals instantly without POST /tools/route.
 */

const URL_RE = /https?:\/\/[^\s<>'")\]]+/gi;
const DATE_RE = /\b(20\d{2}-\d{2}-\d{2})\b/g;
const ALERT_RETRIEVAL_VERB_RE = /\b(?:search|find|locate|open|access|show|get|view|list|look\s+(?:for|up))\b/i;
const ALERT_NOUN_RE = /\balerts?\b/i;

/**
 * Phrases that indicate the user is DISCUSSING or ASKING ABOUT a concept
 * rather than requesting to USE a tool.  Checked before any keyword matching.
 */
const DISCUSSION_SIGNALS = [
  'what is ',
  'what are ',
  'what does ',
  'what do ',
  'how does ',
  'how do ',
  'how would ',
  'how can ',
  'can you explain',
  'please explain',
  'could you explain',
  'explain to me',
  'explain what',
  'explain how',
  'tell me about',
  'tell me what',
  'tell me how',
  'describe ',
  'define ',
  'definition of',
  'i want to understand',
  'i want to know about',
  'i want to know what',
  "i'm asking about",
  'i am asking',
  'i was asking',
  'i read about',
  'i heard about',
  'i was reading',
  "i've been reading",
  'the document',
  'this document',
  'the pdf',
  'this pdf',
  'the article',
  'the file',
  'difference between',
  'compare ',
  'comparison of',
  'hypothetically',
  'for example',
  'can you help me understand',
  'who would use',
  'when would you use',
  'is it possible to',
  'is there a way to',
  'give me information',
  'give me an overview',
  'in general',
  'in theory',
  'what does it mean',
  'what does that mean',
];

const PROTEST_TERMS = [
  'protest', 'protests', 'rally', 'rallies', 'demonstration', 'demonstrations',
  'marches', 'strikes', 'vigils',
];
// Standalone "march" / "strike" / "vigil" are too generic — only honoured
// when a date signal or another protest term is also present.
const PROTEST_GENERIC_TERMS = ['march', 'strike', 'vigil'];
const PROTEST_MONITOR_CONFIG_RE = /\b(?:configure|manage|edit|update|change|set\s*up)\b[\s\S]{0,50}\b(?:daily\s+)?protest(?:\s+city)?\s+monitor(?:\s+(?:configuration|settings|targets?))?\b/i;

const VIDEO_SCORE_TERMS = [
  'score this video', 'score video', 'dictionary score video', 'video dictionary score',
];
const VIDEO_SUMMARY_TERMS = [
  'summarize video', 'summary video', 'analyze video', 'video summary',
  'summary of this video', 'summary of the video', 'summary of a video',
  'provide a summary', 'give me a summary', 'generate a summary',
];
const TRANSCRIBE_TERMS = [
  'transcribe', 'transcrib', 'trascrib', 'trascribe', 'transcripe', 'transcription',
  'transcript', 'translate video', 'translate this video',
  'translate the video', 'translate youtube', 'translate the audio', 'translate audio',
];
// "subtitles" / "captions" only trigger when actual media is present.
const TRANSCRIBE_MEDIA_ONLY_TERMS = ['subtitles', 'captions'];
const VIDEO_URL_TERMS = [
  'youtube.com', 'youtu.be', 'vimeo.com', 'tiktok.com', 'instagram.com',
  'facebook.com', 'twitter.com', 'x.com', 't.me',
];
// Removed "authentic" — too generic in everyday language.
const DEEPFAKE_TERMS = [
  'deepfake', 'fake video', 'fake image', 'manipulated video', 'ai-generated',
];
// Removed standalone "dictionary" — kept specific compound phrases only.
const DICTIONARY_TERMS = ['dictionary analysis', 'poi analysis', 'analyze poi'];
const FACE_TERMS = ['who is this', 'who is in', 'identify face', 'face recognition', 'recognize face'];
const PLACE_TERMS = [
  'where is this', 'what city', 'which city', 'place recognition', 'recognize place', 'landmark',
];
const AVIATION_REPORT_TERMS = [
  'aviation report', 'aviation brief', 'do aviation report',
  'aviation daily brief', 'generate aviation report',
];
const ENERGY_REPORT_TERMS = [
  'do energy report', 'energy report', 'energy brief',
  'energy daily brief', 'generate energy report',
];
const DATA_CENTER_REPORT_TERMS = [
  'do data centers report', 'data centers report', 'data center report',
  'data center daily brief', 'data centers daily brief',
  'generate data center report', 'generate data centers report',
];
const GAMING_REPORT_TERMS = [
  'do gaming report', 'gaming report', 'gaming weekly brief',
  'gaming sector executive briefing', 'generate gaming report',
  'gaming sector weekly',
];
const US_CHINA_TRADE_REPORT_TERMS = [
  'us china trade report',
  'do us china trade report',
  'china trade dispute update',
  'us-china trade dispute update',
  'us china trade dispute',
];
const LATAM_SUMMARY_TERMS = [
  'do latam summary', 'latam summary', 'latam report',
  'do latam report', 'latin america summary', 'latam crime summary',
];
const LATAM_TO_CSV_RE = /\bdo\s+latam\s+to\s+csv\b/i;
const SEQUOIA_STRUCTURE_RE = /\bdo\s+sequoia\s+structure\b/i;
const TWA_SUMMARY_TERMS = [
  'summary tool', 'twa summary', 'do twa', 'do summary tool',
  'article summary', 'arctic summary', 'acadia summary',
  'threat profile summary', 'create summary tool', 'run summary tool',
];
const GMS_REPORT_TERMS = [
  'gms report', 'gms brief', 'do gms report',
  'gms daily brief', 'generate gms report',
];
const DEEP_RESEARCH_TERMS = [
  'deep research', 'comprehensive report', 'research report', 'investigate thoroughly',
  'multi-source analysis', 'detailed investigation', 'in-depth report', 'thorough research',
];
const STATISTICAL_SUMMARY_TERMS = [
  'statistical summary', 'cyber threat report', 'weekly threat report',
  'cyberattack report', 'cyber weekly report', 'attack summary',
  'threat actor report', 'tg upload', 'weekly attack report',
  'generate threat report', 'analyze cyberattack csv', 'csv threat',
];
const ARGO_GUIDE_TERMS = [
  'argo ng',
  'argo portal',
  'argo guide',
  'answer about argo',
  'how to use argo',
  'how do i use argo',
  'in argo ng',
  'on argo ng',
  'on the argo portal',
  'in the argo portal',
  'argo ng how',
  'argo ng what',
  'argo ng where',
];
const INFOGRAPHIC_TERMS = [
  'infographic',
  'info graphic',
  'create infographic',
  'generate infographic',
  'visualize document',
  'visualize this document',
  'create infographic from this document',
  'create infographic from',
  'document infographic',
  'make infographic',
  'build infographic',
  'turn this document into an infographic',
  'turn this pdf into an infographic',
];
const UPLOAD_MONITOR_TERMS = [
  'upload monitor', 'extremism monitor', 'website scanner', 'threat monitor',
];
const UPLOAD_MONITOR_STATUS_TERMS = [
  'status', 'health', 'healthy', 'down', 'downtime', 'failure', 'failed',
  'last run', 'last scan', 'email delivery', 'turn on', 'turn off', 'enable',
  'disable', 'start', 'stop', 'pause', 'resume',
];

/**
 * Returns true when the user is ASKING ABOUT / DISCUSSING a concept rather
 * than requesting to USE a tool.  Must be checked before any keyword matching.
 *
 * @param {string} message
 * @returns {boolean}
 */
function isDiscussionContext(message) {
  const text = (message || '').toLowerCase();
  return DISCUSSION_SIGNALS.some((signal) => text.includes(signal));
}

function extractDeepResearchQuery(message) {
  const text = (message || '').trim();
  if (!text) return '';
  const lowered = text.toLowerCase();
  for (const term of DEEP_RESEARCH_TERMS) {
    if (lowered.includes(term)) {
      const idx = lowered.indexOf(term);
      const after = text.slice(idx + term.length).trim().replace(/^[:,\-\s]+/, '');
      if (after) return after.slice(0, 2000);
    }
  }
  return text.slice(0, 2000);
}

function containsAny(text, terms) {
  return terms.some((term) => text.includes(term));
}

function isAlertRetrievalRequest(message) {
  const value = message || '';
  return ALERT_NOUN_RE.test(value) && ALERT_RETRIEVAL_VERB_RE.test(value);
}

/**
 * Detect if the message requests a specific TWA template.
 * Returns the template name or '' (will default to 'arctic').
 */
function extractTwaTemplate(text) {
  const lowered = (text || '').toLowerCase();
  if (lowered.includes('threat_profile') || lowered.includes('threat profile')) {
    return 'threat_profile';
  }
  if (lowered.includes('acadia')) {
    return 'acadia';
  }
  if (lowered.includes('summary_container') || lowered.includes('summary container')) {
    return 'summary_container';
  }
  if (lowered.includes('arctic')) {
    return 'arctic';
  }
  return '';
}

/**
 * Extract pasted article text from a message: strip any URLs and TWA trigger
 * terms; if what remains is a substantial body of text, return it as an array
 * of pasted text strings.
 */
function extractPastedTexts(message, urls) {
  let text = (message || '').trim();

  // Remove all detected URLs from the message body
  (urls || []).forEach((url) => {
    text = text.replace(url, '');
  });

  // Remove TWA trigger terms (case-insensitive)
  for (const term of TWA_SUMMARY_TERMS) {
    const re = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    text = text.replace(re, '');
  }

  // Remove template keywords that might have been stripped above
  text = text
    .replace(/\b(arctic|acadia|threat[_\s]profile|summary[_\s]container)\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  // Only return as pasted text if it looks like a real article (>= 80 chars)
  if (text.length >= 80) {
    return [text];
  }
  return [];
}

export function extractLatamToCsvReport(message) {
  return (message || '')
    .replace(LATAM_TO_CSV_RE, '')
    .replace(/^[\s:,-]+/, '')
    .trim();
}

export function extractSequoiaStructureReport(message) {
  return (message || '')
    .replace(SEQUOIA_STRUCTURE_RE, '')
    .replace(/^[\s:,-]+/, '')
    .trim();
}

function extractUrls(message) {
  const urls = [];
  const re = new RegExp(URL_RE.source, URL_RE.flags);
  let match;
  while ((match = re.exec(message || '')) !== null) {
    const cleaned = match[0].replace(/[.,;:]+$/, '');
    if (cleaned) urls.push(cleaned);
  }
  return urls;
}

function firstUrl(message) {
  const urls = extractUrls(message);
  return urls[0] || null;
}

function extractLocationForProtest(message) {
  if (!message) return null;
  const patterns = [
    /\b(?:protests?|rall(?:y|ies)|demonstrations?|marches?|strikes?|vigils?)\s+(?:in|near|around|at)\s+([^,.!?;]+)/i,
    /\b(?:in|near|around|at)\s+([^,.!?;]+)\s+(?:protests?|rall(?:y|ies)|demonstrations?|marches?|strikes?|vigils?)/i,
    /\b(?:about|know about)\s+(?:protests?|rall(?:y|ies)|demonstrations?|marches?|strikes?|vigils?)\s+(?:in|near|around|at)\s+([^,.!?;]+)/i,
  ];
  for (const pattern of patterns) {
    const match = message.match(pattern);
    if (match) {
      const location = match[1].replace(/\s+/g, ' ').trim().replace(/^[\s-]+|[\s-]+$/g, '');
      if (location.length > 1 && location.length <= 120) return location;
    }
  }
  return null;
}

function extractDateRange(message) {
  const text = (message || '').toLowerCase();
  const dates = [...(message || '').matchAll(DATE_RE)].map((m) => m[1]);
  if (dates.length >= 2) return { start_date: dates[0], end_date: dates[1] };
  if (dates.length === 1) return { start_date: dates[0], end_date: dates[0] };

  const today = new Date();
  const iso = today.toISOString().slice(0, 10);
  if (text.includes('today')) return { start_date: iso, end_date: iso };
  if (text.includes('tomorrow')) {
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tIso = tomorrow.toISOString().slice(0, 10);
    return { start_date: tIso, end_date: tIso };
  }
  if (text.includes('next week') || text.includes('upcoming')) {
    const end = new Date(today);
    end.setDate(end.getDate() + 7);
    return { start_date: iso, end_date: end.toISOString().slice(0, 10) };
  }
  return { start_date: null, end_date: null };
}

function isVideoTranslationRequest(text, hasVideo, url) {
  if (!text.includes('translate')) return false;
  return Boolean(
    hasVideo
    || text.includes('video')
    || text.includes('audio')
    || (url && containsAny(url.toLowerCase(), VIDEO_URL_TERMS))
  );
}

function baseDecision(action, toolId, confidence, reason, prefill = {}, missingFields = [], tools = []) {
  return {
    action,
    tool_id: toolId,
    confidence,
    reason,
    prefill,
    missing_fields: missingFields,
    tools,
  };
}

/**
 * Detect UI tool routes synchronously on the client.
 * @param {string} message
 * @param {Object} context - has_image, has_pdf, has_excel, has_video
 * @returns {Object} route decision (open_ui | clarify | run_chat)
 */
export function heuristicRouteIntent(message, context = {}) {
  const text = (message || '').toLowerCase();
  const url = firstUrl(message);
  const hasImage = Boolean(context.has_image);
  const hasVideo = Boolean(context.has_video);
  const hasMedia = hasImage || hasVideo || Boolean(url);
  const monitorStatusRequest = containsAny(text, UPLOAD_MONITOR_TERMS)
    && containsAny(text, UPLOAD_MONITOR_STATUS_TERMS);

  // Configuration requests must win over the generic protest-search route.
  if (PROTEST_MONITOR_CONFIG_RE.test(message || '')) {
    return {
      ...baseDecision(
        'open_ui',
        'protest_city_monitor_config',
        0.99,
        'Prompt requests configuration of daily protest monitor targets.'
      ),
      ui_type: 'protest_monitor_modal',
    };
  }

  // Infographic requests must win over the discussion-context gate because
  // prompts like "create infographic from this document" contain "this document".
  if (containsAny(text, INFOGRAPHIC_TERMS)) {
    return baseDecision(
      'open_ui',
      'infographic',
      0.96,
      'Prompt requests document-to-infographic generation.',
      {}
    );
  }

  // --- Discussion-context gate (Layer 1 of the intent-aware fix) -----------
  // If the user is asking ABOUT a concept rather than requesting to USE a tool,
  // skip all heuristics and fall through to normal chat.
  // Exception: Argo guide questions naturally use "how do I …" phrasing, so
  // we check those terms before the discussion gate.
  if (
    !LATAM_TO_CSV_RE.test(message || '')
    && !SEQUOIA_STRUCTURE_RE.test(message || '')
    && !containsAny(text, ARGO_GUIDE_TERMS)
    && !monitorStatusRequest
    && isDiscussionContext(message)
  ) {
    return baseDecision('run_chat', 'none', 0, 'Discussion context detected — no tool route.');
  }
  // --------------------------------------------------------------------------

  if (monitorStatusRequest) {
    return baseDecision(
      'run_chat',
      'upload_monitor_status',
      0.99,
      'Prompt asks for operational upload monitor status.',
      {},
      [],
      ['upload_monitor_status']
    );
  }

  if (containsAny(text, AVIATION_REPORT_TERMS)) {
    return baseDecision(
      'start_direct',
      'aviation_report',
      0.97,
      'Prompt requests an Aviation Sector Executive Daily Brief.',
      {
        query: 'do a deep research about summary of threats, including geopolitical and safety ones, to the aviation sector in the past 24 hours',
        scope: '',
        sources: { web: true, communications: false, attachments: false },
        depth: 'deep',
        report_type: 'aviation',
      }
    );
  }

  if (containsAny(text, ENERGY_REPORT_TERMS)) {
    return baseDecision(
      'start_direct',
      'energy_report',
      0.97,
      'Prompt requests an Energy Sector Executive Daily Brief.',
      {
        query: 'do a deep research about security threats, environmental protests, legislation, M&A and new projects in the energy sector in the past 24 hours',
        scope: '',
        sources: { web: true, communications: false, attachments: false },
        depth: 'deep',
        report_type: 'energy',
      }
    );
  }

  if (containsAny(text, DATA_CENTER_REPORT_TERMS)) {
    return baseDecision(
      'start_direct',
      'data_center_report',
      0.97,
      'Prompt requests a Data Centers Executive Daily Brief.',
      {
        query: 'do a deep research about the data centers sector in the past 24 hours',
        scope: '',
        sources: { web: true, communications: false, attachments: false },
        depth: 'deep',
        report_type: 'data_center',
      }
    );
  }

  if (containsAny(text, GAMING_REPORT_TERMS)) {
    return baseDecision(
      'start_direct',
      'gaming_report',
      0.97,
      'Prompt requests a Gaming Sector Weekly Executive Briefing.',
      {
        query: 'do a gaming report about the video game industry in the past 7 days',
        scope: '',
        sources: { web: true, communications: false, attachments: false },
        depth: 'deep',
        report_type: 'gaming',
      }
    );
  }

  if (containsAny(text, US_CHINA_TRADE_REPORT_TERMS)) {
    return baseDecision(
      'start_direct',
      'us_china_trade',
      0.97,
      'Prompt requests a US-China Trade Dispute weekly update.',
      {
        query: 'us china trade report for the past 7 days',
        scope: '',
        sources: { web: true, communications: false, attachments: false },
        depth: 'deep',
        report_type: 'us_china_trade',
      }
    );
  }

  if (containsAny(text, TWA_SUMMARY_TERMS)) {
    const urls = extractUrls(message);
    const template = extractTwaTemplate(text);
    const pastedTexts = extractPastedTexts(message, urls);
    return baseDecision(
      'start_direct',
      'twa_summary',
      0.97,
      'Prompt requests a TWA cyber-article intelligence summary.',
      { urls, template, pastedTexts }
    );
  }

  if (LATAM_TO_CSV_RE.test(message || '')) {
    return baseDecision(
      'start_direct',
      'latam_to_csv',
      0.99,
      'Prompt requests conversion of a pasted LATAM incident report to TSV.',
      { reportText: extractLatamToCsvReport(message) }
    );
  }

  if (SEQUOIA_STRUCTURE_RE.test(message || '')) {
    return baseDecision(
      'start_direct',
      'sequoia_structure',
      0.99,
      'Prompt requests rewriting a pasted report into a Sequoia GMS brief.',
      { reportText: extractSequoiaStructureReport(message) }
    );
  }

  if (containsAny(text, LATAM_SUMMARY_TERMS)) {
    const urls = extractUrls(message);
    return baseDecision(
      'start_direct',
      'latam_summary',
      0.97,
      'Prompt requests a LATAM crime incident summary from the provided URLs.',
      { urls }
    );
  }

  if (containsAny(text, GMS_REPORT_TERMS)) {
    return baseDecision(
      'start_direct',
      'gms_report',
      0.97,
      'Prompt requests a GMS Daily Intelligence Brief.',
      {
        query: 'do a deep research about maritime security incidents in the past 24 hours covering MARITIME (high seas), AMERICAS, APAC, and EMEA regions',
        scope: '',
        sources: { web: true, communications: false, attachments: false },
        depth: 'deep',
        report_type: 'gms',
      }
    );
  }

  if (containsAny(text, STATISTICAL_SUMMARY_TERMS)) {
    return baseDecision(
      'open_ui',
      'statistical_summary',
      0.97,
      'Prompt requests a cyberattack statistical summary report from a CSV.',
      {}
    );
  }

  if (containsAny(text, DEEP_RESEARCH_TERMS)) {
    const query = extractDeepResearchQuery(message);
    const missing = query ? [] : ['research_query'];
    return baseDecision(
      'open_ui',
      'deep_research',
      0.93,
      'Prompt asks for a comprehensive multi-source research report.',
      {
        query: query || message.trim(),
        scope: '',
        sources: { web: true, communications: true, attachments: true },
        depth: 'standard',
      },
      missing
    );
  }

  // Alert-history retrieval owns these requests even when their search topic
  // is "protest". Sending normal chat lets the backend build the Argo NG link.
  if (isAlertRetrievalRequest(message)) {
    return baseDecision(
      'run_chat',
      'none',
      0.99,
      'Prompt asks to retrieve alerts in Argo NG history.'
    );
  }

  // Protest: require core multi-word protest terms OR single-word protest/rally
  // combined with a date/location signal.  Generic "march"/"strike"/"vigil" alone
  // are not enough — they must co-occur with another protest term or a date.
  const hasDateSignal = DATE_RE.test(message || '')
    || ['today', 'tomorrow', 'next week', 'upcoming', 'this week'].some((w) => text.includes(w));
  const hasProtestCore = containsAny(text, PROTEST_TERMS);
  const hasProtestGeneric = containsAny(text, PROTEST_GENERIC_TERMS);
  if (hasProtestCore || (hasProtestGeneric && (hasDateSignal || hasProtestCore))) {
    const dates = extractDateRange(message);
    const missing = [];
    if (!dates.start_date) missing.push('start_date');
    if (!dates.end_date) missing.push('end_date');
    return baseDecision(
      'open_ui',
      'protest',
      0.92,
      'Prompt asks for protest intelligence.',
      {
        location: extractLocationForProtest(message) || '',
        start_date: dates.start_date,
        end_date: dates.end_date,
        query_template: 'upcoming_protests',
      },
      missing
    );
  }

  // Transcribe: core terms require "video" in text or media present.
  // Media-only terms (subtitles, captions) require actual media.
  const hasTranscribeCore = containsAny(text, TRANSCRIBE_TERMS) && (text.includes('video') || hasMedia);
  const hasTranscribeMedia = containsAny(text, TRANSCRIBE_MEDIA_ONLY_TERMS) && hasMedia;
  if (hasTranscribeCore || hasTranscribeMedia || isVideoTranslationRequest(text, hasVideo, url)) {
    const missing = url || hasVideo ? [] : ['video_file_or_url'];
    return baseDecision(
      'open_ui',
      'transcribe_video',
      0.9,
      'Prompt asks for a video transcript or non-English-to-English translation.',
      { url, mode: 'transcribe' },
      missing
    );
  }

  if (containsAny(text, VIDEO_SCORE_TERMS)) {
    if (url || hasVideo) {
      return baseDecision(
        'start_direct',
        'score_video',
        0.95,
        'Prompt asks to score a video against Carinae dictionaries.',
        { url },
        []
      );
    }
    return baseDecision(
      'clarify',
      'score_video',
      0.9,
      'Video dictionary scoring requires a video URL or attachment.',
      {},
      ['video_file_or_url']
    );
  }

  if (
    containsAny(text, VIDEO_SUMMARY_TERMS)
    || ((text.includes('summarize') || text.includes('summary')) && (text.includes('video') || hasVideo))
    || (hasVideo && !text.trim())
  ) {
    const missing = url || hasVideo ? [] : ['video_file_or_url'];
    return baseDecision(
      'open_ui',
      'summarize_video',
      0.88,
      'Prompt asks for video summarization.',
      { url, prompt: message, mode: 'summarize' },
      missing
    );
  }

  // Deepfake: "authentic" removed from DEEPFAKE_TERMS (too generic).
  // "fake" alone only fires when media is present.
  if (containsAny(text, DEEPFAKE_TERMS) || (hasMedia && text.includes('fake'))) {
    const missing = hasMedia ? [] : ['media_file_or_url'];
    return baseDecision(
      'open_ui',
      'deepfake_detection',
      0.9,
      'Prompt asks for deepfake detection.',
      { url },
      missing
    );
  }

  // Dictionary: standalone "dictionary" removed; require compound phrases or
  // a tamcglobal media URL.
  if (containsAny(text, DICTIONARY_TERMS) || (text.includes('media.tamcglobal.com') && url)) {
    const missing = url || hasImage ? [] : ['image_or_url'];
    return baseDecision(
      'open_ui',
      'dictionary_analysis',
      0.85,
      'Prompt asks for dictionary/POI analysis.',
      { image_url: url },
      missing
    );
  }

  if (!hasImage && containsAny(text, FACE_TERMS)) {
    return baseDecision(
      'clarify',
      'face_recognition',
      0.82,
      'This tool needs an image attachment.',
      {},
      ['image']
    );
  }

  if (!hasImage && containsAny(text, PLACE_TERMS)) {
    return baseDecision(
      'clarify',
      'place_recognition',
      0.82,
      'This tool needs an image attachment.',
      {},
      ['image']
    );
  }

  if (containsAny(text, ARGO_GUIDE_TERMS)) {
    return baseDecision(
      'run_chat',
      'argo_guide',
      0.93,
      'Prompt asks a question about the Argo NG Portal.',
      {},
      [],
      ['argo_guide']
    );
  }

  if (url && containsAny(url.toLowerCase(), VIDEO_URL_TERMS)) {
    return baseDecision(
      'open_ui',
      'summarize_video',
      0.75,
      'Message contains a video URL — opening video summarizer.',
      { url, prompt: message, mode: 'summarize' },
      []
    );
  }

  return baseDecision('run_chat', 'none', 0, 'No UI tool route detected.');
}
